import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
}

interface SimpleToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const COOKIE_PREFERENCES_KEY = 'cookie-preferences';
const COOKIE_CONSENT_VERSION = '1.0';

// Simple styled checkbox toggle with proper TypeScript types
function SimpleToggle({ checked, onChange, disabled = false }: SimpleToggleProps) {
  return (
    <label className="inline-flex items-center">
      <div className={`w-12 h-6 relative rounded-full ${checked ? 'bg-primary' : 'bg-gray-300'} ${disabled ? 'opacity-60' : ''}`}>
        <input 
          type="checkbox" 
          className="sr-only"
          checked={checked}
          onChange={() => !disabled && onChange(!checked)}
          disabled={disabled}
        />
        <div 
          className={`absolute w-5 h-5 rounded-full bg-white top-0.5 shadow transition-all duration-200 ${checked ? 'left-[calc(100%-21px)]' : 'left-0.5'}`} 
        />
      </div>
    </label>
  );
}

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always required
    analytics: false,
    advertising: false
  });

  useEffect(() => {
    const savedPreferences = Cookies.get(COOKIE_PREFERENCES_KEY);
    if (!savedPreferences) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(savedPreferences);
        if (parsed.version !== COOKIE_CONSENT_VERSION) {
          setShowBanner(true);
        } else {
          setPreferences(parsed.preferences);
        }
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    setPreferences(prefs);
    Cookies.set(COOKIE_PREFERENCES_KEY, JSON.stringify({
      version: COOKIE_CONSENT_VERSION,
      preferences: prefs
    }), { expires: 365 });
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      advertising: true
    });
  };

  const rejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      advertising: false
    });
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50"
          >
            <div className="max-w-7xl mx-auto p-4 sm:px-6 lg:px-8">
              <div className="md:flex md:items-center md:justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-medium text-gray-900 mb-2">Cookie Preferences</h2>
                  <p className="text-sm text-gray-600 mb-4 md:mb-0">
                    We use cookies to enhance your browsing experience and analyze our traffic. See our{' '}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> for details.
                  </p>
                </div>
                <div className="mt-4 flex flex-shrink-0 md:mt-0 md:ml-4 space-x-3">
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Customize
                  </button>
                  <button
                    onClick={rejectAll}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={acceptAll}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog.Root open={showPreferences} onOpenChange={setShowPreferences}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-start justify-between mb-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Cookie Preferences
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="rounded-full p-2 hover:bg-gray-100">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </Dialog.Close>
            </div>

            <div className="space-y-5">
              <div className="flex flex-row items-center justify-between">
                <div className="pr-4">
                  <h3 className="text-sm font-medium text-gray-900">Essential Cookies</h3>
                  <p className="text-sm text-gray-500">Required for the website to function properly</p>
                </div>
                <SimpleToggle checked={true} onChange={() => {}} disabled={true} />
              </div>

              <div className="flex flex-row items-center justify-between">
                <div className="pr-4">
                  <h3 className="text-sm font-medium text-gray-900">Analytics Cookies</h3>
                  <p className="text-sm text-gray-500">Help us improve our website by collecting usage information</p>
                </div>
                <SimpleToggle 
                  checked={preferences.analytics} 
                  onChange={(checked: boolean) => setPreferences(p => ({ ...p, analytics: checked }))} 
                />
              </div>

              <div className="flex flex-row items-center justify-between">
                <div className="pr-4">
                  <h3 className="text-sm font-medium text-gray-900">Advertising Cookies</h3>
                  <p className="text-sm text-gray-500">Used to show relevant advertisements and measure their performance</p>
                </div>
                <SimpleToggle 
                  checked={preferences.advertising} 
                  onChange={(checked: boolean) => setPreferences(p => ({ ...p, advertising: checked }))} 
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => savePreferences(preferences)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
              >
                Save Preferences
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}